/* ===============================
   RPG — formatos e leituras do contrato da API publica
   (docs/api-publica-rpg.md no repositorio da API)

   Tudo aqui aceita dado faltando: um jogador sem Roblox, sem crew ou com
   posicao null nunca pode derrubar a pagina.
=============================== */

/* ===============================
   🔢 NUMEROS E DATAS
================================ */
const eNumero = (valor) => typeof valor === "number" && Number.isFinite(valor);

export function formatar_numero(valor) {
  return eNumero(valor) ? valor.toLocaleString("pt-BR") : "0";
}

// 1.250.000 -> "1,3 mi" (para os totais das crews)
export function formatar_compacto(valor) {
  if (!eNumero(valor)) return "0";
  return new Intl.NumberFormat("pt-BR", { notation: "compact", maximumFractionDigits: 1 }).format(valor);
}

// unidade vem do contrato: "vezes" -> "12×", "pts" -> "340 pts", "" -> "1.250.000"
export function formatar_valor(valor, unidade) {
  const numero = formatar_numero(valor);
  if (unidade === "vezes") return `${numero}×`;
  if (unidade === "pts") return `${numero} pts`;
  return numero;
}

// cabecalho da coluna de valor no ranking de uma categoria
export function rotulo_da_unidade(unidade) {
  return unidade === "vezes" ? "Vezes" : "Total";
}

// o que o valor de uma categoria significa (pagina de regras e ranking)
export function explicacao_da_unidade(unidade) {
  if (unidade === "vezes") return "conta quantas vezes cada atividade foi feita";
  if (unidade === "pts") return "soma os pts ganhos";
  return "soma a quantidade registrada";
}

// quem leva o Rei, conforme a unidade (pagina de regras)
export function regra_da_unidade(unidade) {
  if (unidade === "vezes") return "Vence quem fez mais vezes.";
  if (unidade === "pts") return "Vence quem somou mais pts.";
  return "Vence quem registrou a maior quantidade.";
}

export function texto_posicao(posicao) {
  return eNumero(posicao) && posicao > 0 ? `#${posicao}` : "—";
}

export function formatar_data(iso) {
  if (!iso) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  return data.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

export function formatar_data_hora(iso) {
  if (!iso) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  return data.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).replace(",", " às");
}

/* ===============================
   🔗 LINKS
   O slug do contrato e o nick do Roblox em minusculas (links antigos) ou
   o discordId de quem nao tem Roblox.
================================ */
export function link_do_jogador(jogador) {
  const id = jogador?.slug || jogador?.discordId;
  return id ? `/player/${encodeURIComponent(id)}` : null;
}

export function link_da_crew(crew) {
  return crew?.slug ? `/crews/${encodeURIComponent(crew.slug)}` : null;
}

export function link_da_categoria(chave) {
  return chave ? `/rankings/${encodeURIComponent(chave)}` : "/rankings";
}

/* ===============================
   🎨 CORES (vem do painel como #RRGGBB)
================================ */
const COR_PADRAO = "#9b4dff";

export function cor_segura(cor) {
  return /^#[0-9a-fA-F]{6}$/.test(String(cor || "")) ? cor : COR_PADRAO;
}

// "#ff4d4d" + 0.2 -> "#ff4d4d33"
export function com_alfa(cor, alfa) {
  const hex = Math.round(Math.min(1, Math.max(0, alfa)) * 255).toString(16).padStart(2, "0");
  return `${cor_segura(cor)}${hex}`;
}

/* ===============================
   🏅 INSIGNIAS
================================ */
export const RARIDADES = {
  COMMON:    { nome: "Comum",    cor: "#9ca3af" },
  UNCOMMON:  { nome: "Incomum",  cor: "#22c55e" },
  RARE:      { nome: "Rara",     cor: "#3b82f6" },
  EPIC:      { nome: "Épica",    cor: "#a855f7" },
  LEGENDARY: { nome: "Lendária", cor: "#f59e0b" },
  MYTHIC:    { nome: "Mítica",   cor: "#ef4444" },
};

export function raridade(chave) {
  return RARIDADES[String(chave || "").toUpperCase()] || { nome: chave || "Comum", cor: RARIDADES.COMMON.cor };
}

/* ===============================
   📚 CATALOGO (GET /rpg/catalogo)
================================ */
export const GRUPOS_DE_ATIVIDADE = [
  { grupo: "mar",       nome: "Eventos do Mar",   descricao: "Derrote as ameaças do mar com a crew ou com quem estiver no servidor." },
  { grupo: "chefe",     nome: "Chefes",           descricao: "Os chefes mais difíceis do jogo dão mais XP." },
  { grupo: "raid",      nome: "Raids e Dungeons", descricao: "Cada raid ou dungeon completa conta." },
  { grupo: "pvp",       nome: "PvP",              descricao: "Vitória vale mais, mas até a derrota dá XP." },
  { grupo: "quiz",      nome: "Quiz",             descricao: "Cada acerto no quiz dá XP; pergunta mais difícil vale mais." },
  { grupo: "conquista", nome: "Conquistas",       descricao: "Cada conquista vale pontos, e cada ponto vira XP." },
  { grupo: "recurso",   nome: "Recursos",         descricao: "Cada registro aprovado de gold ou fragmentos dá XP; a quantidade conta para os Reis." },
];

export function nome_do_grupo(grupo) {
  return GRUPOS_DE_ATIVIDADE.find((g) => g.grupo === grupo)?.nome || "Outras atividades";
}

export function atividade_do_catalogo(Catalogo, key) {
  return (Catalogo?.atividades || []).find((a) => a?.key === key) || null;
}

// categorias do ranking (uma por Rei ativo): so as que tem chave, sem repetir
export function categorias_do_catalogo(Catalogo) {
  const lista = Array.isArray(Catalogo?.categorias) ? Catalogo.categorias : [];
  const vistas = new Set();
  return lista.filter((categoria) => {
    const chave = categoria?.chave;
    if (!chave || vistas.has(chave)) return false;
    vistas.add(chave);
    return true;
  });
}

// "🥇 PvP vitória · 🥈 PvP derrota" (chave que o catalogo nao conhece aparece crua)
export function atividades_da_categoria(categoria, Catalogo) {
  return (categoria?.atividades || []).map((key) => {
    const atividade = atividade_do_catalogo(Catalogo, key);
    return atividade ? `${atividade.emoji || ""} ${atividade.nome}`.trim() : key;
  }).join(" · ");
}

// "+750 XP", "+20 XP por ponto", "+250 XP por registro"
export function texto_xp_atividade(atividade) {
  if (!atividade) return "";
  const xp = Number(atividade.xp) || 0;
  const porPonto = Number(atividade.xpPorPonto) || 0;
  if (atividade.usaPontos) {
    const textoPonto = `+${formatar_numero(porPonto)} XP por ponto`;
    return xp > 0 ? `+${formatar_numero(xp)} XP e ${textoPonto}` : textoPonto;
  }
  return `+${formatar_numero(xp)} XP${atividade.contaQuantidade ? " por registro" : ""}`;
}

// categorias antigas (/rankings/pvp, /rankings/sea-events...) -> atividade que a categoria nova contem
const CATEGORIAS_ANTIGAS = {
  raids: "raid", raids_completed: "raid",
  pvp: "pvp_win", wins: "pvp_win",
  "sea-events": "leviathan", sea_events: "leviathan",
  gold: "gold",
  fragments: "fragments",
  achievements: "achievement", conquistas: "achievement",
};

/* Resolve o endereco de /rankings/<slug>:
   - undefined ou "global": ranking global;
   - chave de categoria do catalogo, ou nome antigo que casa com uma;
   - null: nao existe. */
export function resolver_categoria(slug, categorias = []) {
  if (!slug || slug === "global") return "global";
  const alvo = String(slug).toLowerCase();
  const direta = categorias.find((c) => String(c?.chave || "").toLowerCase() === alvo);
  if (direta) return direta;
  const atividade = CATEGORIAS_ANTIGAS[alvo];
  if (atividade) return categorias.find((c) => (c?.atividades || []).includes(atividade)) || null;
  return null;
}

/* ===============================
   📈 CURVA DE NIVEL
   XP do nivel L para o L+1 = round(20 × L^1,38 + 40). A lista oficial vem
   do catalogo; o calculo local so cobre a API fora do ar.
================================ */
export const xp_para_proximo_nivel = (nivel) => Math.round(20 * Math.pow(nivel, 1.38) + 40);

export function niveis_da_curva(Catalogo) {
  const niveis = Catalogo?.curva?.niveis;
  if (Array.isArray(niveis) && niveis.length) return niveis;
  const lista = [{ nivel: 1, xpTotal: 0 }];
  for (let nivel = 1; nivel < 100; nivel++) {
    lista.push({ nivel: nivel + 1, xpTotal: lista[nivel - 1].xpTotal + xp_para_proximo_nivel(nivel) });
  }
  return lista;
}

// XP para passar do nivel ao seguinte, lido da curva do catalogo (a formula so cobre o que faltar)
export function xp_do_nivel(niveis, nivel) {
  const atual   = niveis.find((n) => n?.nivel === nivel);
  const proximo = niveis.find((n) => n?.nivel === nivel + 1);
  if (atual && proximo) return (Number(proximo.xpTotal) || 0) - (Number(atual.xpTotal) || 0);
  return xp_para_proximo_nivel(nivel);
}

/* Progresso montado a partir do XP, no mesmo formato do contrato. A API ja
   manda "progresso"; isto so cobre um jogador que venha sem ele. */
const NIVEL_MAXIMO = 500;

export function progresso_do_xp(xp, Catalogo) {
  const total  = Math.max(0, Number(xp) || 0);
  const niveis = niveis_da_curva(Catalogo);
  let nivel    = 1;
  let inicio   = 0;
  while (nivel < NIVEL_MAXIMO) {
    const passo = xp_do_nivel(niveis, nivel);
    if (!(passo > 0) || inicio + passo > total) break;
    inicio += passo;
    nivel++;
  }
  if (nivel >= NIVEL_MAXIMO) return { nivel, xpTotal: total, xpNoNivel: total - inicio, xpDoNivel: 0, falta: 0, percentual: 100 };

  const xpDoNivel = xp_do_nivel(niveis, nivel);
  const xpNoNivel = total - inicio;
  return {
    nivel,
    xpTotal: total,
    xpNoNivel,
    xpDoNivel,
    falta: Math.max(0, xpDoNivel - xpNoNivel),
    percentual: Math.min(100, Math.floor((xpNoNivel / xpDoNivel) * 100)),
  };
}
